import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import type { BillingStatus } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

/**
 * The run-up to a due date, on every page (operator's request).
 *
 * Billing falls on the 1st, and the wall only appears once a month is
 * actually owed — which makes the day it arrives a surprise unless something
 * says it is coming. So from FOURTEEN DAYS out this counts down on every
 * page, and stops the moment the invoice is paid: paying moves paidThrough
 * to the next unpaid month, the gap grows past fourteen days, and the banner
 * removes itself with no other state to clear.
 *
 * The tone escalates as the date closes — quiet at two weeks, warning inside
 * a week, urgent in the last two days — because a banner that looks the same
 * on day 14 and day 1 trains people to ignore it.
 *
 * It shares the query key the wall uses, so this costs no extra request.
 */
const DAY = 24 * 60 * 60_000;
const WINDOW_DAYS = 14;

export function BillingCountdown() {
  const { session, activeOrg } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';

  const { data } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.get<BillingStatus>('/billing/status'),
    // The platform owner is never billed by their own platform, and a
    // superadmin outside an org has nothing to be due.
    enabled: Boolean(session) && !isSuperadmin && Boolean(activeOrg),
    refetchInterval: 60_000,
    retry: false,
  });

  if (!data || data.blocked) return null; // walled: the page itself says it
  if (data.paidThrough === null) return null; // nothing billable yet
  // The month this counts down to bills nothing (every chassis exempt, or
  // billing switched off): there is no invoice coming, so no countdown.
  if (data.upcomingMonthlyMinor <= 0) return null;

  const daysLeft = Math.ceil((data.paidThrough - Date.now()) / DAY);
  if (daysLeft > WINDOW_DAYS || daysLeft < 0) return null;

  const tone =
    daysLeft <= 2
      ? 'border-danger/40 bg-danger-soft text-danger'
      : daysLeft <= 7
        ? 'border-warn/40 bg-warn-soft text-warn'
        : 'border-info/30 bg-info-soft text-info';

  const when =
    daysLeft === 0 ? 'today' : daysLeft === 1 ? 'tomorrow' : `in ${daysLeft} days`;
  const due = new Date(data.paidThrough).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

  /*
    Whether the card will settle the month this counts down to. That month
    has not arrived — this banner only shows while nothing is owed — so the
    tick's retry budget for it is untouched and what matters is whether the
    card CAN be charged at all: the switch on, not expired, and the platform
    able to charge it (`unavailable` is Paystack off, the rate unset, or the
    card's currency unpriced — the states in which "renews automatically"
    was a promise nobody was going to keep). The API computes `renewal` from
    the tick's own rules, so the banner and the tick cannot disagree.
  */
  const card = data.card;
  const renews =
    card !== null &&
    card.renewal !== 'off' &&
    card.renewal !== 'expired' &&
    card.renewal !== 'unavailable';

  return (
    <div
      // Not printed: it sits above every page including an invoice, and a
      // nag banner across the top of a document handed to a customer is not
      // the operator's best look.
      className={`print:hidden mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm ${tone}`}
      role="status"
    >
      <CalendarClock className="size-4 shrink-0" />
      <span>
        {/* The count of the month being counted down to, not this month's:
            a chassis leaving its free month on the 1st is on that invoice
            and was not on this month's — "for 0 OLTs" was this month's count
            (2026-09-18). */}
        <span className="font-medium">Your next invoice is due {when}</span> — {due}, for{' '}
        {data.upcomingBillableOlts} OLT{data.upcomingBillableOlts === 1 ? '' : 's'}.
        {/* A card on file settles it without anybody doing anything, and a
            banner that says "pay now" over a bill that pays itself trains
            people to ignore the banner. Said here, and the link stays. */}
        {renews && card && <> Renews automatically on •••• {card.last4 ?? '????'}.</>}
      </span>
      <Link to="/billing" className="ml-auto font-medium underline underline-offset-2">
        {renews ? 'Billing' : 'Pay now'}
      </Link>
    </div>
  );
}
